import React, { useState, useEffect } from 'react';
import { Bell, Clock, ExternalLink, Trash2, X, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import api from '../services/api';
import { Link } from 'react-router-dom';

const Notifications = () => {
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/my');
      setNotifications(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => 
        n._id === id ? { ...n, isRead: true } : n
      ));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const deleteNotification = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this notification?')) return;
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter(n => n._id !== id));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all notifications?')) return;
    try {
      await api.delete('/notifications/clear-all');
      setNotifications([]);
    } catch (err) {
      console.error('Error clearing notifications:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unread = notifications.filter(n => !n.isRead);
      if (unread.length === 0) return;
      await Promise.all(unread.map(n => api.put(`/notifications/${n._id}/read`)));
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.background }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4" style={{ borderColor: theme.primary }}></div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="min-h-screen py-8 md:py-20" style={{ backgroundColor: theme.background }}>
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10 md:mb-12">
          <div className="flex items-center space-x-4">
            <div className="p-4 rounded-2xl md:rounded-3xl shadow-lg" style={{ backgroundColor: theme.primary }}>
              <Bell size={24} style={{ color: theme.background }} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter" style={{ color: theme.text }}>
                Alerts
              </h1>
              <p className="text-[10px] font-black uppercase tracking-widest opacity-40">
                {unreadCount > 0 ? `${unreadCount} unread messages` : 'All caught up'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {unreadCount > 0 && (
              <button 
                onClick={markAllAsRead}
                className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-4 py-3 rounded-xl md:rounded-2xl border-2 font-black uppercase tracking-widest text-[9px] transition-all active:scale-95"
                style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.card }}
              >
                <CheckCircle2 size={14} />
                <span>Mark Read</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button 
                onClick={clearAll}
                className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-4 py-3 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] transition-all active:scale-95 bg-red-500 bg-opacity-10"
                style={{ color: '#ef4444' }}
              >
                <Trash2 size={14} />
                <span>Clear All</span>
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div 
            className="text-center py-20 md:py-32 rounded-[40px] border-2 border-dashed" 
            style={{ borderColor: theme.border, backgroundColor: `${theme.surface}40` }}
          >
            <Bell size={64} className="mx-auto mb-6 opacity-10" style={{ color: theme.text }} />
            <h2 className="text-xl font-black uppercase tracking-widest opacity-40">No notifications</h2>
            <p className="text-[10px] font-bold opacity-20 uppercase tracking-[0.2em] mt-2">Your inbox is empty</p>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {notifications.map((notification) => (
              <div 
                key={notification._id}
                onClick={() => !notification.isRead && markAsRead(notification._id)}
                className={`group p-5 md:p-8 rounded-[30px] md:rounded-[40px] border-2 transition-all duration-300 relative overflow-hidden ${!notification.isRead ? 'cursor-pointer active:scale-[0.98]' : ''}`}
                style={{ 
                  backgroundColor: theme.card,
                  borderColor: !notification.isRead ? theme.primary : theme.border,
                  opacity: notification.isRead ? 0.8 : 1,
                  boxShadow: !notification.isRead ? `0 10px 30px -10px ${theme.primary}40` : 'none'
                }}
              >
                {/* Mobile Delete Button (Always Visible on mobile, hover on desktop) */}
                <button 
                  onClick={(e) => deleteNotification(e, notification._id)}
                  className="absolute top-4 right-4 md:top-6 md:right-6 p-3 md:p-2 rounded-xl transition-all md:opacity-0 md:group-hover:opacity-100 bg-gray-500 bg-opacity-5 hover:bg-red-500 hover:bg-opacity-10"
                  style={{ color: theme.text }}
                >
                  <X size={18} />
                </button>

                <div className="flex flex-col space-y-4">
                  <div className="pr-12">
                    <div className="flex items-center space-x-3 mb-2">
                       {!notification.isRead && (
                        <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: theme.primary }}></div>
                      )}
                      <h3 className="font-black uppercase tracking-tight text-base md:text-lg leading-tight" style={{ color: theme.text }}>
                        {notification.title}
                      </h3>
                    </div>
                    <p className="text-xs md:text-sm font-bold opacity-60 leading-relaxed" style={{ color: theme.text }}>
                      {notification.body}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4 md:pt-6 border-t border-dashed" style={{ borderColor: theme.border }}>
                    <div className="flex items-center space-x-2 opacity-40">
                      <Clock size={14} />
                      <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest">
                        {new Date(notification.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {notification.data?.orderId && (
                      <Link 
                        to={`/orders`}
                        className="flex items-center space-x-2 px-4 py-2 rounded-xl border text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                        style={{ color: theme.primary, borderColor: `${theme.primary}40`, backgroundColor: `${theme.primary}10` }}
                      >
                        <ExternalLink size={12} />
                        <span>Track Order</span>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
